import React from 'react';
import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  CADMetadataPanel,
  CADAIAnalysisPanel,
  CADChatPanel,
} from '../../components/cad';
import { CADMarker, CADModel } from '../../types/cad';

interface CadViewerInspectorPanelProps {
  rightTab: number;
  onTabChange: (tab: number) => void;
  selectedModel: CADModel | null;
  onAnalysisComplete: (analysis: string) => void;
  onMarkersUpdate: (markers: CADMarker[]) => void;
  onChatDirtyChange: (dirty: boolean) => void;
  comparisonModelId?: string;
  comparisonModelName?: string;
}

/**
 * Right column: the Metadata / AI Analysis / Chat tabs. Every panel stays
 * mounted (shown/hidden with CSS) so the chat transcript and the viewer's
 * WebGL context survive tab switches. Markup is unchanged from the inline
 * block it replaced.
 */
const CadViewerInspectorPanel: React.FC<CadViewerInspectorPanelProps> = ({
  rightTab,
  onTabChange,
  selectedModel,
  onAnalysisComplete,
  onMarkersUpdate,
  onChatDirtyChange,
  comparisonModelId,
  comparisonModelName,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-[300px] shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card">
      <Tabs
        value={String(rightTab)}
        onValueChange={(v) => onTabChange(Number(v))}
        className="w-full"
      >
        <TabsList className="grid h-9 w-full grid-cols-3">
          <TabsTrigger value="0" className="text-xs">{t('cad.metadataTab')}</TabsTrigger>
          <TabsTrigger value="1" className="text-xs">{t('cad.aiAnalysisTab')}</TabsTrigger>
          <TabsTrigger value="2" className="text-xs">{t('cad.chatTab')}</TabsTrigger>
        </TabsList>
      </Tabs>
      <Separator />
      <div className={cn('flex-1 overflow-auto', rightTab === 0 ? 'block' : 'hidden')}>
        <div className="p-3">
          <CADMetadataPanel
            metadata={selectedModel?.metadata || null}
            filename={selectedModel?.filename}
          />
        </div>
      </div>
      <div className={cn('flex-1 overflow-auto', rightTab === 1 ? 'block' : 'hidden')}>
        <div className="p-3">
          <CADAIAnalysisPanel
            modelId={selectedModel?.id || null}
            existingAnalysis={selectedModel?.aiAnalysis || null}
            onAnalysisComplete={onAnalysisComplete}
          />
        </div>
      </div>
      <div className={cn('flex-1 flex-col overflow-hidden', rightTab === 2 ? 'flex' : 'hidden')}>
        <CADChatPanel
          modelId={selectedModel?.id || null}
          modelName={selectedModel?.filename}
          onMarkersUpdate={onMarkersUpdate}
          onDirtyChange={onChatDirtyChange}
          comparisonModelId={comparisonModelId}
          comparisonModelName={comparisonModelName}
        />
      </div>
    </div>
  );
};

export default CadViewerInspectorPanel;
